import type { DoctorAssessment, DoctorReport } from "../surfaces/registry.js";
import { isTimeoutError } from "./network.js";

export const HOSTED_MCP_OUTAGE_TITLE = "Hosted OrgX MCP is unreachable.";

export interface SetupVerificationSummary {
  hostedMcpDegraded: boolean;
  /** True when the only errors are transient network timeouts on auth/workspace. */
  transientTimeouts: boolean;
  status: "degraded" | "error" | "ok" | "warning";
}

export function getSetupVerificationHeadline(summary: SetupVerificationSummary): string {
  switch (summary.status) {
    case "degraded":
      if (summary.transientTimeouts) {
        return "OrgX servers were slow to respond — setup applied, but the final health check timed out. Re-run `wizard doctor` in a moment to confirm.";
      }
      return "Hosted OrgX MCP is down; local setup can still continue.";
    case "error":
      return "Issues detected";
    case "warning":
      return "Warnings detected";
    case "ok":
      return "All systems ready.";
  }
}

function isTimeoutErrorString(value: string | undefined): boolean {
  if (!value) return false;
  return isTimeoutError(new Error(value));
}

function reportErrorsAreOnlyTimeouts(report: DoctorReport | undefined): boolean {
  if (!report) return false;

  const authTimedOut = !report.auth.ok && !report.auth.skipped && isTimeoutErrorString(report.auth.error);
  const workspaceTimedOut =
    !report.workspace.ok && !report.workspace.skipped && isTimeoutErrorString(report.workspace.error);

  // Every reachable service that we could check must actually be reachable —
  // otherwise we have real problems beyond just slow auth/workspace endpoints.
  const reachableServicesOk =
    (report.hostedMcp.skipped || report.hostedMcp.ok) &&
    (report.openclaw.skipped || report.openclaw.ok);

  return reachableServicesOk && (authTimedOut || workspaceTimedOut);
}

export function summarizeSetupVerification(
  assessment: DoctorAssessment,
  report?: DoctorReport,
): SetupVerificationSummary {
  const errors = assessment.issues.filter((issue) => issue.level === "error");
  if (errors.length === 0) {
    return {
      hostedMcpDegraded: false,
      transientTimeouts: false,
      status: assessment.issues.length > 0 ? "warning" : "ok",
    };
  }

  const hostedMcpDegraded = errors.every((issue) => issue.title === HOSTED_MCP_OUTAGE_TITLE);
  if (hostedMcpDegraded) {
    return {
      hostedMcpDegraded: true,
      transientTimeouts: false,
      status: "degraded",
    };
  }

  // If every error is just an auth-or-workspace timeout while the rest of the
  // stack is healthy, treat the run as degraded rather than failed. The setup
  // was already applied successfully; the doctor just couldn't reach the
  // verification endpoint in time. Loud red errors here scare users into
  // thinking their pairing broke when it didn't.
  const authOrWorkspaceTitles = new Set([
    "OrgX user auth could not be verified.",
    "Current workspace lookup failed.",
  ]);
  const onlyAuthOrWorkspaceErrors = errors.every((issue) => authOrWorkspaceTitles.has(issue.title));
  if (onlyAuthOrWorkspaceErrors && reportErrorsAreOnlyTimeouts(report)) {
    return {
      hostedMcpDegraded: false,
      transientTimeouts: true,
      status: "degraded",
    };
  }

  return {
    hostedMcpDegraded: false,
    transientTimeouts: false,
    status: "error",
  };
}
